import * as S from "./styles.ts";
import { AnimatePresence } from "framer-motion";
import { useTypedSelector } from "@/redux/helpers/hooks.ts";
import { useRouter } from "@/lib/helpers/hooks/useRouter.ts";
import useCurrency from "@/lib/helpers/hooks/useCurrency.ts";
import { Button, LoadingSpinner, P } from "@template/components";
import { ICartDropdownProps } from "../cartInterface.ts";
import AdvancedCartItem from "./AdvancedCartItem/index.tsx";
import CartItem from "./CartItem/index.tsx";
import { isRecordingStorageAddon } from "@/lib/helpers/advancedCartItemHelper.ts";

const CartDropdown = ({
  footer,
  advanced,
  reviewOrder,
  setRenew,
  renew,
}: ICartDropdownProps) => {
  const router = useRouter();
  const currency = useCurrency();
  const isGBP = currency === "£";

  const { cart, user } = useTypedSelector((state) => state.user);
  const incVat = isGBP && cart.cost_gross > 0;

  const hasVoIPBundleUpgrade = cart.items.some(
    (item) => item.type.name === "VoIP Plan Change"
  );
  const hasUser = cart.items.some((item) => item.type.name === "User");

  // Step 2: Filter based on conditions
  const filteredItems = cart.items.filter((item) => {
    if (hasVoIPBundleUpgrade || hasUser) {
      return !isRecordingStorageAddon(item);
    }
    return true;
  });

  if (user.loading) {
    return <LoadingSpinner width={48} />;
  }

  return (
    <>
      <S.CartDropdownContainer>
        <S.CartItemsWrapper $advanced={advanced}>
          {advanced && !reviewOrder && (
            <S.BasketTitle>Billing Summary</S.BasketTitle>
          )}
          <AnimatePresence mode="popLayout">
            {[...filteredItems]
              .sort((a, b) => {
                if (a.type.name < b.type.name) {
                  return -1;
                }
                return 0;
              })
              .map((item) =>
                advanced ? (
                  <AdvancedCartItem
                    item={item}
                    billing_interval={cart?.billing_interval}
                    key={item.id}
                    reviewOrder={reviewOrder}
                    setRenew={setRenew}
                    renew={renew}
                    isPaymentReceipt={false}
                  />
                ) : (
                  <CartItem
                    item={item}
                    key={item.id}
                    billing_interval={cart?.billing_interval}
                  />
                )
              )}
          </AnimatePresence>
        </S.CartItemsWrapper>
        <S.CartFooter>
          {incVat && advanced && (
            <>
              <S.TotalContainer>
                <S.TotalLabel>Total ex VAT</S.TotalLabel>
                <S.TotalValue>
                  <span>{currency}</span>
                  {(cart.cost_net / 100).toFixed(2)}
                </S.TotalValue>
              </S.TotalContainer>
              <S.TotalContainer>
                <S.TotalLabel>VAT (20%)</S.TotalLabel>
                <S.TotalValue>
                  <span>{currency}</span>
                  {((cart.cost_gross - cart.cost_net) / 100).toFixed(2)}
                </S.TotalValue>
              </S.TotalContainer>
            </>
          )}
          <S.TotalContainer>
            <S.TotalLabel>
              Total {incVat && advanced && `(${filteredItems.length} items)`}
            </S.TotalLabel>
            <S.TotalValue>
              <span>{currency}</span>
              {(cart.cost_gross / 100).toFixed(2)}
              {incVat && !advanced && (
                <P
                  as="span"
                  $small
                >
                  {" "}
                  (Inc. VAT)
                </P>
              )}
            </S.TotalValue>
          </S.TotalContainer>
          {footer ? (
            footer
          ) : (
            <S.FooterButton>
              <Button
                size="md"
                onClick={() => {
                  router.push("/shopping-cart");
                }}
              >
                Checkout
              </Button>
            </S.FooterButton>
          )}
        </S.CartFooter>
      </S.CartDropdownContainer>
    </>
  );
};

export default CartDropdown;
